// src/components/eframes/EFrame215.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveHeader,
  Card,
} from "../ui";
import { useEFrameData } from "./hooks/useEFrameData";
import { getPlanOwnerName } from "../../utils/planOwnerLabels";
import { DocumentsIcon } from "../ui/Icons";
import type { AppDocument } from "../../types/document";
import BackToExecutorDashboardButton from "./BackToExecutorDashboardButton";

interface LegalDocument {
  id: string;
  type: "will" | "trust" | "healthcare_power_of_attorney" | "healthcare_directive" | "other";
  name: string;
  description?: string;
  uploadDate: string;
  lastModified?: string;
  notes?: string;
  documents: AppDocument[];
}

interface Executor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const DOCUMENT_TYPES = {
  will: { label: "Last Will and Testament" },
  trust: { label: "Living Trust" },
  healthcare_power_of_attorney: { label: "Healthcare Power of Attorney" },
  healthcare_directive: { label: "Healthcare Directive" },
  other: { label: "Other Legal Documents" },
} as const;

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  if (!Array.isArray(docs)) return [];

  return docs
    .filter((doc): doc is Record<string, unknown> => !!doc && typeof doc === "object")
    .map((doc) => ({
      id: typeof doc.id === "string" ? doc.id : "",
      name: typeof doc.name === "string" ? doc.name : "Untitled document",
      mimeType: typeof doc.mimeType === "string" ? doc.mimeType : undefined,
      size: typeof doc.size === "number" ? doc.size : undefined,
    }))
    .filter((doc) => !!doc.id);
};

const EFrame215: React.FC = () => {
  const { estateId } = useParams<{ estateId: string }>();
  const { session } = useEFrameData(estateId ?? null);
  const planOwnerName = getPlanOwnerName(session.name);

  const [documents, setDocuments] = useState<LegalDocument[]>([]);
  const [executors, setExecutors] = useState<Executor[]>([]);

  useEffect(() => {
    const savedDocs = session.taskProgress?.legal_documents?.answers?.documents ?? [];

    setDocuments(
      savedDocs.map((doc: LegalDocument) => ({
        ...doc,
        documents: normalizeDocuments(doc.documents || []),
      }))
    );

    const fromExecutorsTask =
      session.taskProgress?.executors?.answers?.selectedExecutors ?? null;

    const fromLegacyLegalEssentials =
      session.taskProgress?.legal_essentials?.answers?.executors ?? [];

    const resolvedExecutors = Array.isArray(fromExecutorsTask)
      ? fromExecutorsTask
      : fromLegacyLegalEssentials;

    setExecutors(resolvedExecutors);
  }, [
    session.taskProgress?.legal_documents?.answers?.documents,
    session.taskProgress?.executors?.answers?.selectedExecutors,
    session.taskProgress?.legal_essentials?.answers?.executors,
  ]);

  const documentsByType = useMemo(() => {
    return documents.reduce((acc, doc) => {
      const type = doc.type;
      if (!acc[type]) acc[type] = [];
      acc[type].push(doc);
      return acc;
    }, {} as Record<LegalDocument["type"], LegalDocument[]>);
  }, [documents]);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="mb-6 flex justify-start">
          <BackToExecutorDashboardButton />
        </div>
        <ResponsiveHeader title={`${planOwnerName}'s Legal and Executor Summary`} />

        <div className="space-y-6">
          <Card>
            <h3 className="text-lg font-semibold text-stone-800 mb-4">
              Documents Overview
            </h3>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">
                  {documents.length}
                </div>
                <div className="text-sm text-stone-600">Total Documents</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {Object.keys(documentsByType).length}
                </div>
                <div className="text-sm text-stone-600">Categories</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {documents.filter((doc) => doc.notes).length}
                </div>
                <div className="text-sm text-stone-600">With Notes</div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Executor{executors.length > 1 ? "s" : ""}
              </h3>
            </div>

            {executors.length > 0 ? (
              <div className="space-y-3">
                {executors.map((executor) => (
                  <div key={executor.id} className="bg-stone-50 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="font-medium text-stone-800">
                          {executor.firstName} {executor.lastName}
                        </h4>
                        <p className="text-sm text-stone-600 mt-1">
                          {executor.email}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <p>No executors selected.</p>
              </div>
            )}
          </Card>

          <Card>
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Legal Documents
              </h3>
            </div>

            <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {documents.length > 0 ? (
                <div className="space-y-8">
                  {Object.entries(documentsByType).map(([type, docs]) => (
                    <div key={type}>
                      <h4 className="text-sm font-bold text-stone-400 uppercase tracking-widest mb-3 border-b border-stone-100 pb-1">
                        {DOCUMENT_TYPES[type as keyof typeof DOCUMENT_TYPES].label}{" "}
                        ({docs.length})
                      </h4>

                      <div className="pl-7 space-y-2">
                        {docs.map((doc) => {
                          const attachedDocs = normalizeDocuments(doc.documents || []);

                          return (
                            <div key={doc.id} className="bg-stone-50 rounded-lg p-3">
                              <div className="flex-1 min-w-0">
                                <div
                                  className="text-sm font-medium text-stone-800 mb-2 truncate"
                                  title={doc.name}
                                >
                                  {doc.name}
                                </div>

                                {attachedDocs.length > 0 && (
                                  <div className="space-y-2">
                                    {attachedDocs.map((attachedDoc, index) => (
                                      <div
                                        key={`${doc.id}-${attachedDoc.id}-${index}`}
                                        className="flex items-start gap-2 rounded-md border border-stone-200 bg-white px-3 py-2"
                                      >
                                        <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />

                                        <span
                                          className="text-sm font-medium text-stone-800 break-words whitespace-normal min-w-0"
                                          title={attachedDoc.name}
                                        >
                                          {attachedDoc.name}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {doc.notes && (
                                  <p className="text-sm text-stone-600 mt-2 break-words">
                                    {doc.notes}
                                  </p>
                                )}

                                <div className="text-xs text-stone-500 mt-2">
                                  Uploaded {new Date(doc.uploadDate).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <p>No legal documents available.</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EFrame215;